

import { useState } from "react"
import { ChevronDown } from "lucide-react"

interface FAQItem {
  question: string
  answer: string
}

interface FAQSectionProps {
  title?: string
  description?: string
  items: FAQItem[]
  columns?: 1 | 2
}

export function FAQSection({
  title = "Frequently Asked Questions",
  description,
  items,
  columns = 1
}: FAQSectionProps) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  // Split items into columns if needed
  const midpoint = Math.ceil(items.length / 2)
  const leftItems = columns === 2 ? items.slice(0, midpoint) : items
  const rightItems = columns === 2 ? items.slice(midpoint) : []

  const renderItem = (item: FAQItem, index: number, offset: number = 0) => {
    const actualIndex = offset + index
    const isOpen = openIndex === actualIndex

    return (
      <div
        key={actualIndex}
        className="border-b last:border-b-0"
      >
        <button
          onClick={() => toggle(actualIndex)}
          className="flex w-full items-center justify-between py-4 text-left font-medium hover:text-primary transition-colors"
        >
          <span>{item.question}</span>
          <ChevronDown
            className={`h-5 w-5 text-muted-foreground transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </button>
        <div
          className={`overflow-hidden transition-all ${
            isOpen ? 'max-h-96 pb-4' : 'max-h-0'
          }`}
        >
          <p className="text-muted-foreground leading-relaxed">{item.answer}</p>
        </div>
      </div>
    )
  }

  return (
    <section className="py-16 sm:py-24">
      <div className="container px-6">
        <div className="mx-auto max-w-2xl text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
          {description && (
            <p className="mt-4 text-lg text-muted-foreground">{description}</p>
          )}
        </div>

        {columns === 1 ? (
          <div className="mx-auto max-w-3xl">
            {leftItems.map((item, index) => renderItem(item, index))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div>{leftItems.map((item, index) => renderItem(item, index, 0))}</div>
            <div>{rightItems.map((item, index) => renderItem(item, index, midpoint))}</div>
          </div>
        )}
      </div>

      {/* FAQ Schema markup for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: items.map((item) => ({
              "@type": "Question",
              name: item.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.answer
              }
            }))
          })
        }}
      />
    </section>
  )
}
